import { FC } from "react";
import { PageHeader } from "shared/page-header/PageHeader";
import { ProfSummaryId } from "utils/useScroll";
import {
    Box,
    Heading,
    Text,
    List,
    ListItem,
    ListIcon,
    Divider,
    Link,
    Flex,
    Icon,
    useColorModeValue
  } from "@chakra-ui/react";
  import { FiDownload, FiCheck,  FiUsers, FiTarget, FiTrendingUp } from "react-icons/fi";

export const ProfessionalSummary: FC = () => {

    const highlightBg = useColorModeValue("blue.50", "blue.900");
  const strengthColor = useColorModeValue("green.600", "green.400");
  const fitColor = useColorModeValue("orange.600", "orange.400");
  const growthColor = useColorModeValue("purple.600", "purple.400");

    return (
        
        <Box id={ProfSummaryId}>
            <PageHeader label="Professional Summaries" />
            <Box maxW="100uv" mx="auto" p={6}>
      <Flex justifyContent="space-between" alignItems="flex-start" mb={6}>
        <Box>
          <Heading as="h1" size="xl" mb={2} color="blue.600">
            Walid Darwish
          </Heading>
          <Text fontSize="lg" color="gray.600">
            Professional Profile Summary
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Based on Insights Discovery® Assessment, 2025
          </Text>
        </Box>
        <Link 
          href="assets/summaries/WalidDarwishReformingDirector.pdf" 
          download
          color="blue.500"
          display="flex"
          alignItems="center"
        >
          <Icon as={FiDownload} mr={2} />
          Download Full Report
        </Link>
      </Flex>

      <Box bg={highlightBg} p={5} borderRadius="md" mb={6}>
        <Heading as="h2" size="md" mb={4} color="blue.700">
          Core Strengths & Leadership Style
        </Heading>
        <List spacing={3}>
          <ListItem display="flex" alignItems="flex-start">
            <ListIcon as={FiCheck} color={strengthColor} mt={1} />
            <Box>
              <Text as="span" fontWeight="bold" color={strengthColor}>
                Decisive Problem-Solver
              </Text>{" "}
              – Logical, analytical, and quick to act in high-pressure situations. Thrives in crisis management.
            </Box>
          </ListItem>
          <ListItem display="flex" alignItems="flex-start">
            <ListIcon as={FiUsers} color={strengthColor} mt={1} />
            <Box>
              <Text as="span" fontWeight="bold" color={strengthColor}>
                Natural Leader
              </Text>{" "}
              – Confident, assertive, and energizes teams with a results-driven approach. Excels in roles requiring autonomy.
            </Box>
          </ListItem>
          <ListItem display="flex" alignItems="flex-start">
            <ListIcon as={FiTarget} color={strengthColor} mt={1} />
            <Box>
              <Text as="span" fontWeight="bold" color={strengthColor}>
                Strategic Organizer
              </Text>{" "}
              – Rapidly structures chaos into actionable plans. Strong long-term vision with a focus on efficiency.
            </Box>
          </ListItem>
          <ListItem display="flex" alignItems="flex-start">
            <ListIcon as={FiTrendingUp} color={strengthColor} mt={1} />
            <Box>
              <Text as="span" fontWeight="bold" color={strengthColor}>
                High-Performance Driver
              </Text>{" "}
              – Competitive, hardworking, and sets ambitious goals for self and teams.
            </Box>
          </ListItem>
        </List>
      </Box>

      <Box mb={6}>
        <Heading as="h2" size="md" mb={4} color="blue.700">
          Value to Employers
        </Heading>
        <List spacing={3}>
          <ListItem>
            <Text as="span" fontWeight="bold">Execution Focus:</Text> Cuts through bureaucracy to deliver tangible outcomes.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold">Innovative Thinker:</Text> Identifies flaws and improves systems pragmatically.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold">Direct Communicator:</Text> Clear, honest, and efficient in discussions.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold">Team Catalyst:</Text> Energizes colleagues under pressure (though may need to balance assertiveness with empathy).
          </ListItem>
        </List>
      </Box>

      <Box mb={6}>
        <Heading as="h2" size="md" mb={4} color="blue.700">
          Ideal Work Environment
        </Heading>
        <List spacing={3}>
          <ListItem>
            <Text as="span" fontWeight="bold" color={fitColor}>Autonomy:</Text> Minimal micromanagement; prefers defining own rules.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold" color={fitColor}>Challenge:</Text> Roles requiring problem-solving, strategy, and measurable impact.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold" color={fitColor}>Fast-Paced Culture:</Text> Thrives in dynamic settings with urgency (e.g., startups, project-based work).
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold" color={fitColor}>Structured Flexibility:</Text> Clear objectives but freedom to innovate.
          </ListItem>
        </List>
      </Box>

      <Box mb={6}>
        <Heading as="h2" size="md" mb={4} color="blue.700">
        Evolving Leadership Skills and Areas of Improvement
        </Heading>
        <List spacing={3}>
          <ListItem>
            <Text as="span" fontWeight="bold" color={growthColor}>Collaborative Listening:</Text> Continuing to deepen my ability to fully understand and integrate diverse perspectives before making decisions.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold" color={growthColor}>Constructive Candor:</Text> Working on delivering feedback with greater empathy and nuance to build stronger connections.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold" color={growthColor}>Strategic Patience:</Text> Learning to better align urgency with team pacing to support sustainable progress.
          </ListItem>
        </List>
      </Box>

      <Box mb={6}>
        <Heading as="h2" size="md" mb={4} color="blue.700">
          Best Fits for Roles
        </Heading>
        <List spacing={3}>
          <ListItem>
            <Text as="span" fontWeight="bold">Leadership:</Text> Director, Operations Manager, Project Lead.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold">Strategy:</Text> Consultant, Business Analyst, Entrepreneur.
          </ListItem>
          <ListItem>
            <Text as="span" fontWeight="bold">High-Impact Teams:</Text> Startups, turnaround projects, growth-stage companies.
          </ListItem>
        </List>
      </Box>

      <Divider my={6} />

      
    </Box>            
        </Box>
    
    );
}